import { logError } from '@zg-rentals/log-error';
import { SEND_MESSAGE_MAX_LENGTH } from '../constants/ConfigConstants';
import { getCurrentSessionURL } from './fullStory';

type ErrorContext = Record<string, unknown>;

type AppError = {
  message?: string;
  context?: ErrorContext;
  conversationId?: string;
  error?: any;
};

const isUserAuthError = (err: any) => {
  // Proxit clients throw `DatarouterError` (from `@zg-rentals/web-base`) which carries
  // the HTTP `responseStatus`. Duck-type against the field so SLW doesn't need to pull in
  // `web-base` directly. Falls back to the legacy substring match for Apollo errors
  // (whose `err.message` typically reads "...User not authorized...").
  const status = err?.responseStatus;
  if (typeof status === 'number' && (status === 401 || status === 403)) {
    return true;
  }
  return hasWordInErrorMessage(err, 'user');
};

const hasWordInErrorMessage = (err: Error, word: string) => {
  if (!err || !err.message) {
    return false;
  }

  const lowercaseMessage = err.message.toLowerCase();
  return lowercaseMessage.indexOf(word) > -1;
};

/**
 * Best-effort HTTP status extraction across the error shapes we see in practice:
 *  - Proxit `DatarouterError` (from `@zg-rentals/web-base`) carries `responseStatus` directly
 *    (the common case for Proxit clients).
 *  - Some fetch/axios-shaped errors expose `response.status`.
 *  - Some legacy callers stash the status on `status`.
 *
 * Returns `undefined` when we can't determine a status (e.g. a pure network error) — callers
 * should treat that as "recoverable" (keep retrying/polling).
 */
const getErrorHttpStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const e = error as { responseStatus?: unknown; response?: { status?: unknown }; status?: unknown };
  if (typeof e.responseStatus === 'number') {
    return e.responseStatus;
  }
  if (typeof e.response?.status === 'number') {
    return e.response.status;
  }
  if (typeof e.status === 'number') {
    return e.status;
  }
  return undefined;
};

/**
 * True when the error is a 4xx HTTP response. A 4xx is non-recoverable for a fixed request
 * (unauthorized, forbidden, stale/not-found, bad input), so callers should stop
 * retrying / polling / refetching rather than hammering the endpoint.
 */
const is4xxError = (error: unknown): boolean => {
  const status = getErrorHttpStatus(error);
  return status !== undefined && status >= 400 && status < 500;
};

/** True when a send was rejected for exceeding the backend's message-length limit. With no machine-readable
 *  error code, the 400's copy or an over-limit sent length is the best available signal; a miss degrades to
 *  the generic failure. */
const isMessageTooLongError = (error: unknown, sentMessage: string): boolean =>
  getErrorHttpStatus(error) === 400 &&
  (sentMessage.length > SEND_MESSAGE_MAX_LENGTH ||
    (error instanceof Error && /'message' is too long/i.test(error.message)));

/**
 * Best-effort user-facing message for a failed Proxit request. Proxit clients throw
 * `DatarouterError` (from `@zg-rentals/web-base`) whose `message` carries the backend's
 * human-readable text — e.g. the locale-restriction validation message returned by
 * `setScreeningCriteria`. Only surfaced for client errors (4xx); 5xx / network / unknown
 * failures aren't user-appropriate, so callers get the provided fallback. Duck-typed so SLW
 * doesn't need to depend on web-base directly (matches the rest of this module).
 *
 * The transport-error path in `ts-client-http-client` builds the error message from the raw
 * response body (HTML/JSON), so guard against displaying a serialized payload: only surface
 * messages that look like plain prose, otherwise fall back.
 */
const getErrorDisplayMessage = (error: unknown, fallback: string): string => {
  if (is4xxError(error) && error instanceof Error) {
    const message = error.message.trim();
    const looksLikeRawPayload = /^[{[<]/.test(message);
    if (message !== '' && !looksLikeRawPayload) {
      return message;
    }
  }
  return fallback;
};

const formatForLog = (err: any): any => {
  return {
    error: {
      code: err?.code,
      stack: err?.stack,
      message: err?.message,
      data: err?.data,
    },
  };
};

const logAppError = ({ conversationId = '', error = '', context = {}, message = '' }: AppError): void => {
  if (isUserAuthError(error)) {
    return;
  }

  const formattedError = formatForLog(error);
  const fullStorySessionUrl = getCurrentSessionURL();

  logError({ error, context: { conversationId, formattedError, message, fullStorySessionUrl, ...context } });
};

/**
 * True when the error is a 401 or 403 HTTP response — used to gate the WrongAccountAlert
 * takeover UI. Intentionally narrower than `isUserAuthError`: no substring fallback, so a
 * message that happens to contain "user" can't trigger a full-page auth-error state.
 */
const isHttpAuthError = (error: unknown): boolean => {
  const status = getErrorHttpStatus(error);
  return status === 401 || status === 403;
};

export { getErrorDisplayMessage, getErrorHttpStatus, is4xxError, isHttpAuthError, isMessageTooLongError, logAppError };
